import { Suspense, lazy, type ReactNode } from "react";
import { createBrowserRouter, Navigate, useParams } from "react-router-dom";
import { RootLayout } from "./layouts/root-layout";
import { AuthLayout } from "./layouts/auth-layout";
import { DashboardLayout } from "./layouts/dashboard-layout";
import { RequireAuth, RequireWorkflowAccess, RequireUserAdmin } from "./layouts/access-guards";

const LoginPage = lazy(() =>
	import("./features/auth").then((module) => ({ default: module.LoginPage })),
);
const RegisterPage = lazy(() =>
	import("./features/auth").then((module) => ({ default: module.RegisterPage })),
);
const FulfillmentDateTestPage = lazy(() =>
	import("./features/test").then((module) => ({ default: module.FulfillmentDateTestPage })),
);
const NotificationsTestPage = lazy(() =>
	import("./features/test").then((module) => ({ default: module.NotificationsTestPage })),
);
const CollectionsMain = lazy(() =>
	import("./features/collections").then((module) => ({ default: module.CollectionsMain })),
);
const CollectionDetail = lazy(() =>
	import("./features/collections").then((module) => ({ default: module.CollectionDetail })),
);
const PrintMain = lazy(() =>
	import("./features/collections").then((module) => ({ default: module.PrintMain })),
);
const UsersMain = lazy(() =>
	import("./features/users").then((module) => ({ default: module.UsersMain })),
);
const WorkflowsMain = lazy(() =>
	import("./features/workflows").then((module) => ({ default: module.WorkflowsMain })),
);
const ProfileMain = lazy(() =>
	import("./features/profile").then((module) => ({ default: module.ProfileMain })),
);
const OrderMain = lazy(() =>
	import("./features/orders").then((module) => ({ default: module.OrderMain })),
);

function withSuspense(element: ReactNode) {
	return <Suspense fallback={null}>{element}</Suspense>;
}

/**
 * Redirects /collections/:id to /collection/:id
 */
function CollectionIdRedirect() {
	const { id } = useParams();
	return <Navigate to={`/collection/${id}`} replace />;
}

/**
 * Application router configuration
 * Uses React Router v7 for client-side routing
 */
export const router = createBrowserRouter([
	{
		path: "/",
		element: <RootLayout />,
		children: [
			// Dashboard routes (protected)
			{
				element: (
					<RequireAuth>
						<DashboardLayout />
					</RequireAuth>
				),
				children: [
					{
						index: true,
						element: <Navigate to="/collections" replace />,
					},
					{
						path: "collection",
						element: <Navigate to="/collections" replace />,
					},
					{
						path: "collections/:id",
						element: <CollectionIdRedirect />,
					},
					{
						path: "profile",
						element: withSuspense(<ProfileMain />),
					},
					{
						path: "user",
						element: <Navigate to="/users" replace />,
					},
					{
						path: "workflow",
						element: <Navigate to="/workflows" replace />,
					},
					{
						path: "workflows",
						element: (
							<RequireWorkflowAccess>{withSuspense(<WorkflowsMain />)}</RequireWorkflowAccess>
						),
					},
					{
						path: "users",
						element: <RequireUserAdmin>{withSuspense(<UsersMain />)}</RequireUserAdmin>,
					},
					{
						path: "collection/:id/print",
						element: withSuspense(<PrintMain />),
					},
					{
						path: "collection/new",
						element: withSuspense(<CollectionDetail mode="create" />),
					},
					{
						path: "collection/:id",
						element: withSuspense(<CollectionDetail />),
					},
					{
						path: "collections",
						element: withSuspense(<CollectionsMain />),
					},
					{
						path: "order/:id",
						element: withSuspense(<OrderMain />),
					},
					{
						path: "test/notifications",
						element: withSuspense(<NotificationsTestPage />),
					},
					{
						path: "test/fulfillment-date",
						element: withSuspense(<FulfillmentDateTestPage />),
					},
				],
			},
			// Auth routes (public)
			{
				element: <AuthLayout />,
				children: [
					{
						path: "login",
						element: withSuspense(<LoginPage />),
					},
					{
						path: "register",
						element: withSuspense(<RegisterPage />),
					},
					{
						path: "register/:token",
						element: withSuspense(<RegisterPage />),
					},
				],
			},
		],
	},
]);
